import type { AvailableArtifact, PlayableArtifact } from '../dto/playableItemDto';
import { notNull } from '../portable/basicTypes';
import type { PlaybackQuality } from '../types/playbackQuality';
import { playableArtifactToQuality } from './playableArtifactToQuality';


/**
 *  Defines priority order to for trying different qualities.
 *
 *  Low:    any web proxy -> any edit proxy -> any highres
 *  Medium: any edit proxy -> any web proxy -> any highres
 *  High:   any highres roxy -> any edit proxy -> any web proxy
 */
export type PlayableArtifactAutoSelectHint = 'low' | 'medium' | 'high';

export interface PlayableArtifactSelectionOptions {
  specificQuality: PlaybackQuality | null,

  autoSelectHint: PlayableArtifactAutoSelectHint,

  blacklistedQualities: PlaybackQuality[],
}

export interface SelectedPlayableArtifact {
  artifact: PlayableArtifact,
  quality: PlaybackQuality,
}

export function selectPlayableArtifact(
  availableArtifacts: AvailableArtifact[],
  options: PlayableArtifactSelectionOptions
): SelectedPlayableArtifact | null {
  const playableArtifactsWithQuality : SelectedPlayableArtifact[] = availableArtifacts
    .filter((artifact) => artifact.playableStatus === 'playable')
    .map((artifact): SelectedPlayableArtifact | null => {
      const quality = playableArtifactToQuality(artifact);
      if (quality === null) {
        return null;
      }

      return { artifact, quality };
    })
    .filter(notNull)
    .filter((artifactAndQuality) => !options.blacklistedQualities.includes(artifactAndQuality.quality));

  const specificQuality = playableArtifactsWithQuality.find((artifactAndQuality) => artifactAndQuality.quality === options.specificQuality);
  if (specificQuality !== undefined) {
    return specificQuality;
  }

  let priority : PlaybackQuality[];
  switch (options.autoSelectHint) {
    case 'low':
      priority = [
        'web-proxy-segmented',
        'web-proxy-flat',
        'edit-proxy-segmented',
        'edit-proxy-flat',
        'original-segmented',
        'original-flat',
      ];
      break;
    case 'medium':
      priority = [
        'edit-proxy-segmented',
        'edit-proxy-flat',
        'web-proxy-segmented',
        'web-proxy-flat',
        'original-segmented',
        'original-flat',
      ];
      break;
    case 'high':
      priority = [
        'original-segmented',
        'original-flat',
        'edit-proxy-segmented',
        'edit-proxy-flat',
        'web-proxy-segmented',
        'web-proxy-flat',
      ];
      break;
  }

  for (const qualityToLookFor of priority) {
    const artifactWithQuality = playableArtifactsWithQuality.find((a) => a.quality === qualityToLookFor);
    if (artifactWithQuality !== undefined) {
      return artifactWithQuality;
    }
  }

  return null;
}
